import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as defaultQuoteItemActions from '@redux/modules/defaultQuoteItem'

import defaultFormState, { requiredFields } from '@models/defaultQuoteItem'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { CreateMultipleDefaultQuoteItemsPayload, DefaultQuoteItemModel, DefaultQuoteItemRequestOptions } from '@models/types'

const stringifyArrayKeys: (keyof DefaultQuoteItemModel)[] = ['options']
const stringifyObjectKeys: (keyof DefaultQuoteItemModel)[] = ['data']

type CreateDefaultQuoteItemParams = {
  defaultQuoteItemParams: Partial<DefaultQuoteItemModel>,
  dispatch: AppDispatch,
  requestOptions?: DefaultQuoteItemRequestOptions,
}

const createDefaultQuoteItem = (params: CreateDefaultQuoteItemParams) => {
  const { dispatch, defaultQuoteItemParams, requestOptions } = params
  const { createDefaultQuoteItem: createFn } = defaultQuoteItemActions

  const updatedParams = mergeAndStringifyEntityObjects({}, defaultQuoteItemParams, stringifyObjectKeys, stringifyArrayKeys)

  return dispatch(createFn(updatedParams, requestOptions))
}

type CreateDefaultQuoteItemsParams = {
  dispatch: AppDispatch,
  itemPayload: CreateMultipleDefaultQuoteItemsPayload,
  requestOptions?: DefaultQuoteItemRequestOptions,
}

const createDefaultQuoteItems = (params: CreateDefaultQuoteItemsParams) => {
  const { dispatch, itemPayload, requestOptions } = params
  const { createDefaultQuoteItems: createFn } = defaultQuoteItemActions

  return dispatch(createFn(itemPayload, requestOptions))
}

type UpdateDefaultQuoteItemParams = {
  defaultQuoteItem: DefaultQuoteItemModel,
  defaultQuoteItemParams: Partial<DefaultQuoteItemModel>,
  dispatch: AppDispatch,
  requestOptions?: DefaultQuoteItemRequestOptions,
}

const updateDefaultQuoteItem = (params: UpdateDefaultQuoteItemParams) => {
  const {
    dispatch, defaultQuoteItem, defaultQuoteItemParams, requestOptions,
  } = params
  const { updateDefaultQuoteItem: updateFn } = defaultQuoteItemActions

  const updatedParams = mergeAndStringifyEntityObjects(defaultQuoteItem, defaultQuoteItemParams, stringifyObjectKeys, stringifyArrayKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteDefaultQuoteItemParams = {
  defaultQuoteItem: DeleteParams<DefaultQuoteItemModel>,
  dispatch: AppDispatch,
}

const deleteDefaultQuoteItem = (params: DeleteDefaultQuoteItemParams) => {
  const { dispatch, defaultQuoteItem } = params
  const { deleteDefaultQuoteItem: deleteFn } = defaultQuoteItemActions

  return dispatch(deleteFn(defaultQuoteItem))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useDefaultQuoteItemForm(
  defaultQuoteItem: Partial<DefaultQuoteItemModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const defaultQuoteItemForm = useForm(
    defaultFormState,
    { entity: defaultQuoteItem, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [defaultQuoteItem.id, defaultQuoteItem.cache_key],
  )

  return {
    ...defaultQuoteItemForm,
  }
}

export const useRelations = (defaultQuoteItem: Partial<DefaultQuoteItemModel> = {}) => {
  const { provider_id } = defaultQuoteItem

  const entities = useSelector(reduxState => reduxState.entities)
  const { providers } = entities

  const provider = provider_id && providers[provider_id] ? providers[provider_id] : {}

  return {
    provider,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: DefaultQuoteItemRequestOptions,
}

function useDefaultQuoteItem(initEntity: Partial<DefaultQuoteItemModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: defaultQuoteItem }: { entity: DefaultQuoteItemModel } = useLatestEntity(initEntity, 'defaultQuoteItems')
  const { id, owner_id, owner_type } = defaultQuoteItem

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  useReduxAction(
    'defaultQuoteItems',
    'loadDefaultQuoteItem',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.defaultQuoteItems)

  const { provider } = useRelations(defaultQuoteItem)

  const isOrderableDefaultQuoteItem = defaultQuoteItem.item_type === 'orderable'

  return {
    callbacks: {
      addOrderableDefaultQuoteItem: () => launchModal({
        callbacks,
        modalKey: 'NewOrderModal',
        payload: {
          agencyId: owner_type == 'Agency' ? owner_id : undefined,
          isDefaultQuoteItem: true,
        },
      }),
      createDefaultQuoteItems: (
        itemPayload: CreateMultipleDefaultQuoteItemsPayload,
        entityOptions?: DefaultQuoteItemRequestOptions,
      ) => (
        createDefaultQuoteItems({ itemPayload, dispatch, requestOptions: entityOptions })
      ),
      createDefaultQuoteItem: (
        defaultQuoteItemParams: Partial<DefaultQuoteItemModel>,
        entityOptions?: DefaultQuoteItemRequestOptions,
      ) => (
        createDefaultQuoteItem({ defaultQuoteItemParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditDefaultQuoteItem: (customPayload?: Record<string, DefaultQuoteItemModel>) => launchModal({
        callbacks,
        modalKey: 'CreateOrEditDefaultQuoteItemModal',
        payload: { defaultQuoteItem, ...customPayload },
      }),
      deleteDefaultQuoteItem: () => deleteDefaultQuoteItem({ dispatch, defaultQuoteItem }),
      newDefaultQuoteItem: () => launchModal({
        callbacks,
        modalKey: 'NewDefaultQuoteItemModal',
        payload: { defaultQuoteItem },
      }),
      updateDefaultQuoteItem: (
        defaultQuoteItemParams: Partial<DefaultQuoteItemModel>,
        entityOptions?: DefaultQuoteItemRequestOptions,
      ) => (
        updateDefaultQuoteItem({
          defaultQuoteItem, defaultQuoteItemParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    defaultQuoteItem,
    deleting,
    provider,
    isOrderableDefaultQuoteItem,
    loading: loading && !loadedIds.includes(defaultQuoteItem.id),
    updating,
  }
}

export type UseDefaultQuoteItemPayload = ReturnType<typeof useDefaultQuoteItem>

export default useDefaultQuoteItem
